const API_BASE = process.env.REACT_APP_API_URL || '/api';

async function request(path, options = {}) {
  const url = `${API_BASE}${path}`;
  const config = {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  };
  if (options.body instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  const response = await fetch(url, config);
  if (!response.ok) {
    // Try JSON first, then plain text, to get the actual error message
    const text = await response.text().catch(() => '');
    let errorMsg;
    try {
      const json = JSON.parse(text);
      errorMsg = json.error || json.message || `HTTP ${response.status}`;
    } catch {
      errorMsg = `HTTP ${response.status}: ${text.substring(0, 200) || response.statusText}`;
    }
    throw new Error(errorMsg);
  }
  return response.json();
}

// ── Auth ──────────────────────────────────────────────────────────
export const auth = {
  getMe: () => request('/auth/me'),
  logout: () => request('/auth/logout', { method: 'POST' }),
};

// ── Segments ──────────────────────────────────────────────────────
export const segments = {
  list: () => request('/segments'),
  get: (id) => request(`/segments/${id}`),
  getTrail: (trailName) => request(`/segments/trail/${encodeURIComponent(trailName)}`),
  create: (data) => request('/segments', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/segments/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => request(`/segments/${id}`, { method: 'DELETE' }),
  toggleAudit: (id) => request(`/segments/${id}/audit`, { method: 'PUT' }),
  assignOwners: (segmentIds, volunteerId) =>
    request('/segments/owners', { method: 'PUT', body: JSON.stringify({ segment_ids: segmentIds, assigned_volunteer_id: volunteerId || null }) }),
  createExpansion: (data) => request('/segments/expansion', { method: 'POST', body: JSON.stringify(data) }),
  addExpansionContact: (segmentId, data) =>
    request(`/segments/${segmentId}/expansion-contacts`, { method: 'POST', body: JSON.stringify(data) }),
  deleteExpansionContact: (contactId) =>
    request(`/segments/expansion-contacts/${contactId}`, { method: 'DELETE' }),
  committeeComments: (segmentId) => request(`/segments/${segmentId}/committee-comments`),
  addCommitteeComment: (segmentId, body) =>
    request(`/segments/${segmentId}/committee-comments`, { method: 'POST', body: JSON.stringify({ body }) }),
  deleteCommitteeComment: (commentId) =>
    request(`/segments/committee-comments/${commentId}`, { method: 'DELETE' }),
  uploadKml: (id, file) => {
    const fd = new FormData();
    fd.append('kml', file);
    return request(`/segments/${id}/kml`, { method: 'POST', body: fd });
  },
};

// ── Photos ────────────────────────────────────────────────────────
export const photos = {
  upload: (segmentId, file, caption) => {
    const fd = new FormData();
    fd.append('photo', file);
    if (caption) fd.append('caption', caption);
    return request(`/segments/${segmentId}/photos`, { method: 'POST', body: fd });
  },
  uploadUrl: (segmentId, data) =>
    request(`/segments/${segmentId}/photos/upload-url`, { method: 'POST', body: JSON.stringify(data) }),
  recordS3: (segmentId, data) =>
    request(`/segments/${segmentId}/photos/s3`, { method: 'POST', body: JSON.stringify(data) }),
  listPending: () => request('/photos/pending'),
  review: (id, status) => request(`/photos/${id}/review`, { method: 'PUT', body: JSON.stringify({ status }) }),
  delete: (id) => request(`/photos/${id}`, { method: 'DELETE' }),
};

// ── Concerns ──────────────────────────────────────────────────────
export const concerns = {
  raise: (segmentId, description) =>
    request(`/segments/${segmentId}/concerns`, { method: 'POST', body: JSON.stringify({ description }) }),
  list: (status) => request(`/concerns${status ? `?status=${status}` : ''}`),
  resolve: (id, resolution_notes) =>
    request(`/concerns/${id}/resolve`, { method: 'PUT', body: JSON.stringify({ resolution_notes }) }),
  acknowledge: (id) => request(`/concerns/${id}/acknowledge`, { method: 'PUT' }),
};

// ── Users ─────────────────────────────────────────────────────────
export const users = {
  list: (filters) => {
    const params = new URLSearchParams(filters).toString();
    return request(`/users${params ? `?${params}` : ''}`);
  },
  listPending: () => request('/users/pending'),
  approve: (id, role) => request(`/users/${id}/approve`, { method: 'PUT', body: JSON.stringify({ role }) }),
  changeRole: (id, data) => request(`/users/${id}/role`, { method: 'PUT', body: JSON.stringify(data) }),
  suspend: (id) => request(`/users/${id}/suspend`, { method: 'PUT' }),
  rename: (id, displayName) => request(`/users/${id}/name`, { method: 'PUT', body: JSON.stringify({ display_name: displayName }) }),
  listVolunteers: () => request('/users/volunteers'),
};

// ── Access Agreement Types ───────────────────────────────────────
export const accessTypes = {
  list: () => request('/access-types'),
  create: (data) => request('/access-types', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/access-types/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => request(`/access-types/${id}`, { method: 'DELETE' }),
};

// ── Waivers ──────────────────────────────────────────────────────
export const waivers = {
  getActive: () => request('/waiver'),
  sign: (data) => request('/waiver/sign', { method: 'POST', body: JSON.stringify(data) }),
  listSignatures: (params) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/admin/waivers/signatures${qs ? `?${qs}` : ''}`);
  },
  exportUrl: (params) => {
    const qs = new URLSearchParams(params).toString();
    return `/api/admin/waivers/export${qs ? `?${qs}` : ''}`;
  },
  listVersions: () => request('/admin/waivers/versions'),
};

// ── Agreement Files ──────────────────────────────────────────────
export const agreementFiles = {
  list: (segmentId) => request(`/segments/${segmentId}/files`),
  upload: async (segmentId, file) => {
    // Step 1: Get presigned S3 URL
    const { uploadUrl, key } = await request('/upload-url', {
      method: 'POST',
      body: JSON.stringify({ file_name: file.name, content_type: file.type || 'application/pdf' }),
    });
    // Step 2: Upload directly to S3 (bypasses CloudFront WAF)
    const s3res = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type || 'application/pdf' },
    });
    if (!s3res.ok) throw new Error('Failed to upload file to storage');
    // Step 3: Record in database
    return request(`/segments/${segmentId}/files/s3`, {
      method: 'POST',
      body: JSON.stringify({ key, file_name: file.name }),
    });
  },
  delete: (id) => request(`/files/${id}`, { method: 'DELETE' }),
  listAll: () => request('/files'),
  link: (fileId, segmentId) => request(`/files/${fileId}/link`, { method: 'POST', body: JSON.stringify({ segment_id: segmentId }) }),
  downloadUrl: (filePath) => {
    // Local files have no slash, S3 files have "agreements/uuid.pdf"
    if (filePath.includes('/')) return null; // S3 files need presigned URL
    return `/uploads/agreements/${filePath}`;
  },
  getDownloadUrl: async (fileId) => {
    const { url } = await request(`/files/${fileId}/download`);
    return url;
  },
};

// ── Maintenance Requests ─────────────────────────────────────────
export const maintenanceRequests = {
  list: (status) => request(`/maintenance-requests${status ? `?status=${status}` : ''}`),
  get: (id) => request(`/maintenance-requests/${id}`),
  create: (data) => request('/maintenance-requests', { method: 'POST', body: JSON.stringify(data) }),
  send: (id) => request(`/maintenance-requests/${id}/send`, { method: 'PUT' }),
  recordQuote: (id, vendor_name) =>
    request(`/maintenance-requests/${id}/quote`, { method: 'PUT', body: JSON.stringify({ vendor_name }) }),
  approve: (id) => request(`/maintenance-requests/${id}/approve`, { method: 'PUT' }),
  complete: (id) => request(`/maintenance-requests/${id}/complete`, { method: 'PUT' }),
};

// ── Meeting Scheduler ────────────────────────────────────────
export const meetings = {
  getActive: () => request('/meetings/active'),
  saveAvailability: (slots) =>
    request('/meetings/active/availability', { method: 'PUT', body: JSON.stringify({ slots }) }),
  conclude: (chosen_datetime) =>
    request('/meetings/conclude', { method: 'POST', body: JSON.stringify({ chosen_datetime }) }),
  getHistory: () => request('/meetings/history'),
  create: (month) =>
    request('/meetings/create', { method: 'POST', body: JSON.stringify({ month }) }),
};

// ── System Settings ──────────────────────────────────────────────
export const systemSettings = {
  get: () => request('/settings'),
  update: (settings) => request('/settings', { method: 'PUT', body: JSON.stringify({ settings }) }),
  getPublic: () => request('/settings/public'),
};

// ── Volunteer Portal ─────────────────────────────────────────────
export const portal = {
  getOverview: () => request('/portal/overview'),
  listVolunteers: () => request('/portal/volunteers'),
  mySegments: () => request('/portal/my-segments'),
  listSegments: () => request('/portal/segments'),

  listMeetings: () => request('/portal/meetings'),
  getMeeting: (id) => request(`/portal/meetings/${id}`),
  createMeeting: (data) => request('/portal/meetings', { method: 'POST', body: JSON.stringify(data) }),
  updateMeeting: (id, data) => request(`/portal/meetings/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  publishMinutes: (id, publish = true) =>
    request(`/portal/meetings/${id}/publish`, { method: 'POST', body: JSON.stringify({ publish }) }),
  deleteMeeting: (id) => request(`/portal/meetings/${id}`, { method: 'DELETE' }),
  minutesPdfUrl: (id) => `${API_BASE}/portal/meetings/${id}/minutes.pdf`,
  minutesRecipients: () => request('/portal/minutes-recipients'),
  emailMinutes: (id, to, cc, note, replyTo) =>
    request(`/portal/meetings/${id}/email-minutes`, { method: 'POST', body: JSON.stringify({ to, cc, note, reply_to: replyTo }) }),
  notifyMeeting: (id, opts = {}) =>
    request(`/portal/meetings/${id}/notify`, { method: 'POST', body: JSON.stringify(opts) }),

  // Meeting runner
  getTopics: (meetingId) => request(`/portal/meetings/${meetingId}/topics`),
  generateTopics: (meetingId) =>
    request(`/portal/meetings/${meetingId}/topics/generate`, { method: 'POST' }),
  updateTopic: (topicId, data) =>
    request(`/portal/topics/${topicId}`, { method: 'PUT', body: JSON.stringify(data) }),
  attachTopicSegment: (topicId, segment_id) =>
    request(`/portal/topics/${topicId}/segments`, { method: 'POST', body: JSON.stringify({ segment_id }) }),
  detachTopicSegment: (topicId, segmentId) =>
    request(`/portal/topics/${topicId}/segments/${segmentId}`, { method: 'DELETE' }),
  compileTopics: (meetingId, save) =>
    request(`/portal/meetings/${meetingId}/topics/compile`, { method: 'POST', body: JSON.stringify({ save }) }),
  startDraft: (meetingId) =>
    request(`/portal/meetings/${meetingId}/topics/draft`, { method: 'POST' }),
  getDraft: (meetingId) => request(`/portal/meetings/${meetingId}/topics/draft`),

  listActions: (filters) => {
    const params = new URLSearchParams(
      Object.entries(filters || {}).filter(([, v]) => v !== '' && v != null)
    ).toString();
    return request(`/portal/actions${params ? `?${params}` : ''}`);
  },
  getAction: (id) => request(`/portal/actions/${id}`),
  createAction: (data) => request('/portal/actions', { method: 'POST', body: JSON.stringify(data) }),
  updateAction: (id, data) => request(`/portal/actions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteAction: (id) => request(`/portal/actions/${id}`, { method: 'DELETE' }),

  addActionUpdate: (id, body) =>
    request(`/portal/actions/${id}/updates`, { method: 'POST', body: JSON.stringify({ body }) }),
  deleteActionUpdate: (id) => request(`/portal/updates/${id}`, { method: 'DELETE' }),
};
